<template>
	<div v-if="hasEditorAccess" class="landing-page">
		<!-- Header Section -->
		<CustomSection wave style="background: linear-gradient(to left, #00d4ff, #006b8f); margin-top: 0">
			<div class="header-div">
				<v-row class="header-row">
					<v-col cols="12" md="7">
						<div class="header-content">
							<h1 class="headline">Complaint Management</h1>
							<!-- <p class="header-text">AI-assisted complaint management</p> -->
						</div>
					</v-col>
				</v-row>
			</div>
		</CustomSection>

		<!-- Tab Navigation -->
		<div class="tabs-container">
			<v-tabs v-model="view" class="view-tabs" color="#006b8f" grow>
				<v-tab value="roleplay" class="view-tab">
					<v-icon start>mdi-robot</v-icon>
					AI Roleplay
				</v-tab>
				<v-tab value="livecalls" class="view-tab">
					<v-icon start>mdi-phone-in-talk</v-icon>
					Live Call
				</v-tab>
			</v-tabs>
		</div>

		<!-- Cases Section -->
		<CustomSection
			v-show="view === 'roleplay'"
			class="content-section"
			style="background-color: rgb(var(--v-theme-background))"
		>
			<div class="section-div">
				<v-row>
					<!-- <h1 class="title">Wählen Sie einen Fall</h1> -->
					<v-col cols="12">
						<!-- Cases Loading State -->
						<div v-if="loadingCases" class="loading-section">
							<v-progress-circular indeterminate color="#1486c9" size="64"></v-progress-circular>
							<p class="loading-text">Loading scenarios...</p>
						</div>

						<!-- Cases Grid -->
						<v-row v-else-if="cases.length > 0 && hasEditorAccess" class="cases-row" justify="center">
							<v-col
								v-for="(case_, i) in cases"
								:key="case_.botId"
								cols="12"
								sm="8"
								:md="cases.length === 1 ? '6' : '6'"
								:lg="cases.length === 1 ? '6' : cases.length === 2 ? '4' : '4'"
							>
								<v-card class="case-card" elevation="10" variant="tonal">
									<!-- <div class="case-image-wrapper">
										<img class="case-image" :src="getCaseImageURL(i)" alt="Case illustration" />
									</div> -->
									<v-card-title class="case-title">
										<b>{{ case_.botName }}</b>
									</v-card-title>
									<v-card-text class="case-text">
										{{ getCaseDescription(i) }}
									</v-card-text>
									<v-card-actions class="case-actions">
										<v-btn
											color="#1486c9"
											variant="elevated"
											block
											:loading="loading && selectedCaseId === case_.botId"
											class="case-button"
											@click="selectCase(case_)"
										>
											Start roleplay
											<template v-slot:loader>
												<v-progress-circular
													indeterminate
													size="20"
													color="white"
												></v-progress-circular>
											</template>
										</v-btn>
									</v-card-actions>
								</v-card>
							</v-col>
						</v-row>
						<!-- No Access -->
						<div v-else-if="!hasEditorAccess" class="no-cases-section">
							<v-icon size="64" color="#ff7043" class="mb-4">mdi-lock</v-icon>
							<p class="no-cases-text">No access</p>
							<p class="no-cases-text">You need to be an editor in this domain to access the scenarios</p>
						</div>

						<!-- No Cases Available -->
						<div v-else-if="cases.length < 1 && hasEditorAccess" class="no-cases-section">
							<v-icon size="64" color="#1486c9" class="mb-4">mdi-robot-off</v-icon>
							<p class="no-cases-text">No scenarios available</p>
						</div>
					</v-col>
				</v-row>
			</div>
		</CustomSection>

		<!-- Live Calls -->
		<CustomSection
			v-show="view === 'livecalls'"
			class="content-section"
			style="background-color: rgb(var(--v-theme-background))"
		>
			<div class="section-div">
				<div style="width: 100%; height: 100%" class="d-flex flex-column justify-center align-center">
					<!-- ALTERNIS SESSIONS -->
					<v-card color="contained" rounded="lg">
						<v-card-text>
							<v-list
								v-if="activeSessions.length > 0"
								style="max-height: 40vh; overflow-y: auto; padding: 0"
								rounded="lg"
							>
								<v-list-item
									v-for="session in activeSessions"
									:key="session.id"
									class="d-flex align-center active-call-item"
									:title="`Call from ${session.label}`"
									subtitle="Active Call"
									prepend-icon="mdi-phone-in-talk"
									@click="() => sessionClick(session.id, session.label)"
									:disabled="loading"
								>
									<template #append>
										<v-icon
											:style="{ visibility: session.clicked ? 'visible' : 'hidden' }"
											color="#00c853"
										>
											mdi-check
										</v-icon>
									</template>
									<template #prepend>
										<v-icon color="#00c853" class="ringing-phone"> mdi-phone-in-talk </v-icon>
									</template>
								</v-list-item>
							</v-list>
							<v-alert v-else>
								<div>No calls available</div>
							</v-alert>
						</v-card-text>
					</v-card>

					<!-- QR Codes Section -->
					<div v-if="BOEHRINGER_TELEPHONE_NUMBERS.some((n) => n.showQr)" class="qr-codes-section">
						<h3 class="qr-codes-title">Phone Numbers</h3>
						<p class="qr-codes-text">
							Scan the QR codes or dial the displayed numbers to call the respective lines. Once the call
							is answered, it will appear in the list above. Click on the entry to create the associated
							workspace and navigate there.
						</p>
						<div class="qr-codes-container">
							<div
								v-for="phone in BOEHRINGER_TELEPHONE_NUMBERS.filter((n) => n.showQr)"
								:key="phone.number"
								class="qr-code-item"
							>
								<vue-qrcode
									:value="`tel:+${phone.number}`"
									:options="{ width: 150, margin: 1 }"
									class="qr-code-image"
								/>

								<div class="qr-code-label">{{ phone.label }}</div>
								<div class="qr-code-number">{{ formatPhoneNumber(phone.number) }}</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</CustomSection>

		<!-- Info Text Section -->
		<!-- <CustomSection style="background-color: rgb(var(--v-theme-background))">
			<div class="section-div">
				<v-row>
					<v-col cols="12">
						<div class="description-section">
							<p class="description-text">
								Wie sieht Berufsberatung aus, wenn Mensch und KI ihre Stärken bündeln? Dieser
								Demonstrator lädt dazu ein, genau das zu erleben. Eine sprachbasierte KI analysiert live
								Gesprächsinhalte, erkennt Werte, Interessen und Einstellungen – und verknüpft sie mit
								über 3.500 Berufseinträgen aus BERUFENET. Das Ergebnis ist eine interaktive, visuell
								unterstützte Beratung, bei der Berufsberater:innen sich auf den versierten Umgang mit
								einer ratsuchenden Person konzentrieren - die KI fokussiert sich hingegen auf die
								Analyse umfangreicher Datenmengen, um sich mit gezielten Hinweisen einzuschalten. Durch
								ein ausgeklügeltes Design der Interaktionen zwischen Mensch und KI bleiben
								Berufsberater:innen jedoch zu jedem Zeitpunkt in der Führungsrolle. Probieren Sie es
								jetzt aus und entdecken Sie das Potenzial symbiotischer Zusammenarbeit zwischen Mensch
								und KI.
							</p>
						</div>
					</v-col>
				</v-row>
			</div>
		</CustomSection> -->

		<!-- Logos Section -->
		<CustomSection style="background-color: rgb(var(--v-theme-background))">
			<div class="section-div">
				<v-row>
					<v-col cols="12">
						<div class="logos-section">
							<div class="logo-container">
								<img src="/dfki_Logo_vz.svg" alt="DFKI Logo" class="logo" />
							</div>
							<div class="logo-container">
								<img :src="BOEHRINGER_LOGO_URL" alt="Boehringer Ingelheim Logo" class="logo" />
							</div>
						</div>
					</v-col>
				</v-row>
			</div>
		</CustomSection>

		<!-- Footer Wave -->
		<CustomSection
			class="last-segment"
			wave
			wave-color="#007095"
			style="background-color: rgb(var(--v-theme-background))"
		>
		</CustomSection>

		<Footer />
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useRouter } from "vue-router";
import { api as operatorAPI } from "@lr-notitia/notitia-operator/client";
import { useDomainStore } from "../../../store/domain";
import VueQrcode from "@chenfengyuan/vue-qrcode";
import CustomSection from "../../itNotruf/components/CustomSection.vue";
import Footer from "../../itNotruf/components/Footer.vue";
import { useInstanceWorkspacesStore } from "../../../store/i-workspace";
import { useInstanceDataspacesStore } from "../../../store/i-dataspace";
import { api as alternisAPI } from "@lr-notitia/notitia-alternis/client";
import { UserRole } from "@lr-notitia/notitia-operator/schemas";

interface AlternisSession {
	id: string;
	label: string;
	reference: string;
	clicked: boolean;
}

const router = useRouter();
const domainStore = useDomainStore();
const instanceWorkspacesStore = useInstanceWorkspacesStore();
const instanceDataspacesStore = useInstanceDataspacesStore();
const activeSessions = ref<AlternisSession[]>([]);
const loading = ref(false);

const DOMAIN_ID = "ac6efd25-5ebd-45fb-ac1e-596a0eb0c9e1"; // Complaint Management
const TEMPLATE_WORKSPACE_ID = "15b78915-6e04-4761-b1d2-094ab38a7436"; // Complain Management Template WS
const INSTANCE_DATASPACE_ID = "7c7dbb51-547d-4804-a4d1-4879762dbd5c"; // Testing
const BOEHRINGER_LOGO_URL = "https://assets.livereader.com/asset/lHOICc4sYPyfBUxw.png";
const BOEHRINGER_TELEPHONE_NUMBERS: { label: string; number: string; showQr: boolean }[] = [
	{ label: "Boehringer Kai Wolf", number: "4968543289644", showQr: false },
	{ label: "Boehringer Carsten Maletzki", number: "4968543289645", showQr: false },
	{ label: "Boehringer Unknown Employee", number: "4968543289646", showQr: false },
	{ label: "Boehringer Felix Sehmer", number: "4968543289647", showQr: false },
];

const loadingCases = ref(false);
const selectedCaseId = ref<string | null>(null);
const view = ref<"roleplay" | "livecalls">("roleplay");

const cases = ref<{ botId: string; botName: string }[]>([]);

const hasEditorAccess = computed(() => {
	return domainStore.hasAccessRights(UserRole.Editor) || domainStore.hasAccessRights(UserRole.Admin);
});

function getCaseDescription(i: number): string {
	const descriptions: string[] = [
		"An upset but determined daughter calls about her elderly father’s sudden breathing problems, insisting his inhaler is defective and demanding real help and accountability.",
		"A financially strained, asthma-dependent patient from Alaska calls to insist her nearly new inhaler was defective and demands a quick, hassle-free replacement while emphasizing she did nothing wrong.",
		"An anxious but determined 83-year-old woman worries that her “sometimes not spraying” asthma inhaler might be faulty, fearing she isn’t getting the medicine she needs to keep breathing safely.",
	];

	return descriptions[i];
}

function getCaseImageURL(i: number): string {
	const images: string[] = [];
	return images[i] || "";
}

async function loadCases() {
	if (!hasEditorAccess.value) return;

	try {
		loadingCases.value = true;
		const bots = await alternisAPI().bots().get(domainStore.account.id, domainStore.domain.id);
		cases.value = bots.sort((a, b) => a.botName.localeCompare(b.botName));
	} catch (error) {
		console.error("Error loading cases:", error);
	} finally {
		loadingCases.value = false;
	}
}

async function selectCase(case_: any) {
	if (loading.value || !hasEditorAccess.value) return;

	loading.value = true;
	selectedCaseId.value = case_.botId;

	try {
		const workspace = await instanceWorkspacesStore.addWorkspace(
			INSTANCE_DATASPACE_ID,
			TEMPLATE_WORKSPACE_ID,
			case_.botName,
			`IT-Notruf erstellt am ${new Date().toLocaleString("de-DE", { timeZone: "Europe/Berlin" })}`,
		);

		await instanceWorkspacesStore.setWorkspaceDescriptor(
			DOMAIN_ID,
			INSTANCE_DATASPACE_ID,
			workspace.id,
			case_.botName,
			`${domainStore.account.firstname} ${domainStore.account.lastname} - ${new Date().toLocaleString("de-DE", {
				timeZone: "Europe/Berlin",
			})}`,
		);

		// Add to dataspace
		const dataspace = await instanceDataspacesStore.getById(INSTANCE_DATASPACE_ID);
		if (dataspace) {
			dataspace.workspaces.push(workspace.id);
			await instanceDataspacesStore.updateDataspace(dataspace);
		}

		await instanceWorkspacesStore.loadWorkspace(DOMAIN_ID, workspace.id);

		// Redirect with bot parameter
		router.push({
			path: `/${DOMAIN_ID}/template/${INSTANCE_DATASPACE_ID}/template/${workspace.id}`,
			query: { botId: case_.botId },
		});
	} catch (error) {
		console.error("Error creating workspace:", error);
	} finally {
		loading.value = false;
		selectedCaseId.value = null;
	}
}

async function loadData() {
	try {
		await instanceDataspacesStore.loadDataspaces(DOMAIN_ID);
		if (hasEditorAccess.value) {
			await loadCases();
		}
	} catch (error) {
		console.error("Error loading IT-Notruf data:", error);
		loadingCases.value = false;
	}
}

async function fetchSessions() {
	try {
		const response = await fetch("/api/aokplus/connectable-sessions", {
			method: "GET",
			headers: {
				accept: "application/json",
			},
		});

		if (!response.ok) {
			console.error("Failed to fetch sessions:", response.statusText);
			return;
		}

		const data = await response.json();

		const newSessions = data
			.filter((session: any) => !activeSessions.value.some((s) => s.id === session.id))
			.filter(
				(session: any) =>
					session.reference && BOEHRINGER_TELEPHONE_NUMBERS.some((t) => session.reference == t.number),
			)
			.map((session: any) => ({
				...session,
				clicked: false,
			}));

		activeSessions.value = [
			...activeSessions.value.filter((s) => data.some((session: any) => session.id === s.id)),
			...newSessions,
		];
	} catch (err) {
		console.error("Error fetching sessions:", err);
	}
}

function formatPhoneNumber(number: string): string {
	// Format: +49 6854 32896 30
	if (number.startsWith("49")) {
		const countryCode = number.slice(0, 2);
		const areaCode = number.slice(2, 6);
		const part1 = number.slice(6, 11);
		const part2 = number.slice(11);
		return `+${countryCode} ${areaCode} ${part1} ${part2}`;
	}
	return `+${number}`;
}

async function sessionClick(sessionId: string, label: string) {
	const session = activeSessions.value.find((s) => s.id === sessionId);
	if (!session) return;

	session.clicked = true;
	loading.value = true;

	const workspaceId = "new_" + sessionId;
	const name = `Anruf von ${label}${
		" an " + BOEHRINGER_TELEPHONE_NUMBERS.find((t) => t.number === session.reference)?.label || ""
	}`;
	const description = `Erstellt am ${new Date().toLocaleString("de-DE", { timeZone: "Europe/Berlin" })} von ${
		domainStore.account.firstname
	} ${domainStore.account.lastname}`;

	const url = `/api/v1.0/${DOMAIN_ID}/external/newWorkspace`;
	const body = {
		dataspaceId: INSTANCE_DATASPACE_ID,
		templateId: TEMPLATE_WORKSPACE_ID,
		name,
		description,
		id: workspaceId,
	};

	const workspaceUrl = `${window.location.origin}/${DOMAIN_ID}/template/${INSTANCE_DATASPACE_ID}/template/${workspaceId}`;

	try {
		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${domainStore.authToken}`,
			},
			body: JSON.stringify(body),
		});

		if (response.ok) {
			const data = await response.json();
			console.log("Workspace created:", data);

			// Post RTP language settings
			setTimeout(() => {
				operatorAPI()
					.domain(DOMAIN_ID)
					.workspace(workspaceId)
					.alternis()
					.postRtpLanguage(0, "soft-locked-de")
					.catch((err: any) => console.error("Error posting rtp language:", err));
				operatorAPI()
					.domain(DOMAIN_ID)
					.workspace(workspaceId)
					.alternis()
					.postRtpLanguage(1, "soft-locked-de")
					.catch((err: any) => console.error("Error posting rtp language:", err));
			}, 1_000);

			window.open(workspaceUrl, "_blank");
		} else if (response.status === 409) {
			console.info(`Workspace ${workspaceId} already exists, opening it.`);
			window.open(workspaceUrl, "_blank");
		} else {
			console.error(`Workspace creation failed with status ${response.status}`);
		}
	} catch (err) {
		console.error("Error creating workspace:", err);
	} finally {
		loading.value = false;
	}
}

onMounted(async () => {
	document.body.classList.add("boehringer-page");
	document.body.style.backgroundColor = "rgb(128, 203, 196)";

	loadingCases.value = true;
	try {
		await domainStore.loadDomain(DOMAIN_ID);
		if (!hasEditorAccess.value) {
			throw new Error("No editor access");
		}
		await loadData();
	} catch (error) {
		router.push({ name: "Domains" });
	}
	try {
		await fetchSessions();
		setInterval(fetchSessions, 2_000);
	} catch (error) {
		console.error("Error fetching sessions:", error);
	}
});

onUnmounted(() => {
	document.body.classList.remove("boehringer-page");
	document.body.style.backgroundColor = "";
});
</script>

<style scoped lang="scss">
.landing-page {
	min-height: 100vh;
	min-height: 100dvh;
	display: flex;
	flex-direction: column;
	background-color: #1a1a1a;
}

.content-section {
	flex: 1;
}

.header-row {
	margin-top: -30px;
	margin-bottom: -30px;
	@media screen and (max-width: 690px) {
		margin-top: -70px;
		margin-bottom: -50px;
	}
}

.headline {
	font-size: 3rem;
	font-family: "STEPS", sans-serif;
	color: #ffffff;
	text-shadow: 2px 2px 1px rgba(0, 0, 0, 0.5);
	@media screen and (max-width: 690px) {
		font-size: 2rem;
	}
}

.header-text {
	font-size: 1.25rem;
	color: #fff;
	text-shadow: 2px 2px 1px rgba(0, 0, 0, 0.5);
	@media screen and (max-width: 690px) {
		font-size: 1rem;
	}
}

.header-div {
	margin: 100px 3rem 100px;
	@media screen and (max-width: 1200px) {
		margin: 100px 2rem 50px;
	}
	@media screen and (max-width: 690px) {
		margin: 100px 1.75rem 50px;
	}
}

.header-content {
	display: flex;
	height: 100%;
	justify-content: center;
	flex-direction: column;
	text-shadow: 0px 0px 10px rgba(0, 0, 0, 0.5);
}

.section-div {
	margin: 60px 3rem 50px;
	@media screen and (max-width: 1200px) {
		margin: 50px 2rem 50px;
	}
	@media screen and (max-width: 690px) {
		margin: 45px 1.75rem 40px;
	}
}

.title {
	color: #1486c9;
	font-size: 3rem;
	text-align: center;
	width: 100%;
	margin-bottom: 2rem;
	text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);
	@media screen and (max-width: 1200px) {
		font-size: 2.3rem;
		white-space: normal;
	}
	@media screen and (max-width: 690px) {
		font-size: 2rem;
	}
}

.loading-section {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 200px;

	.loading-text {
		color: #1486c9;
		font-size: 1.1rem;
		margin-top: 1rem;
	}
}

.no-cases-section {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 200px;

	.no-cases-text {
		color: #1486c9;
		font-size: 1.1rem;
		text-shadow: 0px 0px 1px rgba(0, 0, 0, 0.3);
	}
}

.cases-row {
	margin-bottom: 2rem;
}

.case-card {
	border-radius: 10px;
	padding: 0;
	padding-left: 0.8rem;
	height: 250px;
	transition: transform 0.3s ease, box-shadow 0.3s ease;
	overflow: hidden;
	position: relative;
	display: flex;
	flex-direction: column;

	&::before {
		content: "";
		position: absolute;
		left: 0;
		top: 0;
		bottom: 0;
		width: 0.8rem;
		background-color: #1486c9;
		border-radius: 10px 0 0 10px;
	}

	&[disabled] {
		opacity: 0.7;
		cursor: not-allowed;
	}
}

.case-image-wrapper {
	width: 100%;
	height: 50px;
	overflow: hidden;
	background-color: #1486c9;
	display: flex;
	align-items: center;
	justify-content: center;
}

.case-image {
	width: 100%;
	height: 100%;
	object-fit: cover;
	transition: transform 0.4s ease;
	object-position: 0px -60px;
}

.case-title {
	color: #1486c9;
	font-size: 1.3rem;
	padding: 1.25rem 1rem 0.75rem 1rem;
	@media screen and (max-width: 690px) {
		line-height: 80%;
		font-size: 1.1rem;
		max-width: 100%;
		white-space: pre-wrap;
		word-break: break-word;
	}
}

.case-text {
	font-size: 1rem;
	line-height: 1.5;
	padding: 0 1rem 1rem 1rem;
	flex-grow: 1;
}

.case-icon-wrapper {
	display: flex;
	justify-content: center;
	margin-bottom: 1rem;
}

.case-actions {
	padding: 0 1rem 1rem 1rem;
	margin-top: auto;
}

.case-button {
	font-weight: 600;
	transition: box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1);
	letter-spacing: 0.5px;
	box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);

	&:hover:not([disabled]) {
		background-color: #115d89 !important;
		box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12);
	}

	&:active:not([disabled]) {
		background-color: #114666 !important;
		box-shadow: 0 5px 5px -3px rgba(0, 0, 0, 0.2), 0 8px 10px 1px rgba(0, 0, 0, 0.14),
			0 3px 14px 2px rgba(0, 0, 0, 0.12);
	}
}

.description-section {
	margin: 2rem 0;
	text-align: center;

	.description-text {
		color: #1486c9;
		font-size: 1.1rem;
		line-height: 1.6;
		text-shadow: 0px 0px 1px rgba(0, 0, 0, 0.3);
		max-width: 1000px;
		margin: 0 auto;
		text-align: justify;

		@media screen and (max-width: 690px) {
			font-size: 1rem;
			text-align: left;
		}
	}
}

.logos-section {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 4rem;
	margin: 2rem 0;

	@media screen and (max-width: 768px) {
		flex-direction: column;
		gap: 2rem;
	}

	.logo-container {
		display: flex;
		align-items: center;
		justify-content: center;
		background-color: white;
		border-radius: 10px;
		padding: 15px;
		width: 250px;
		height: 120px;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

		@media screen and (max-width: 690px) {
			width: 200px;
			height: 100px;
			padding: 10px;
		}

		.logo {
			height: auto;
			transition: filter 0.3s ease;
			max-width: 200px;
			max-height: 80px;

			@media screen and (max-width: 690px) {
				max-width: 160px;
				max-height: 70px;
			}
		}
	}
}

@media (max-width: 768px) {
	.header-div {
		margin: 50px 1rem 50px;
	}

	.section-div {
		margin: 30px 1rem 30px;
	}
}

.nonSelect {
	-webkit-touch-callout: none;
	-webkit-user-select: none;
	-khtml-user-select: none;
	-moz-user-select: none;
	-ms-user-select: none;
	user-select: none;
}

svg {
	.affected_person_tag_fill {
		fill: #f4f4f4;
	}

	&.dark {
		.affected_person_tag_fill {
			fill: #404040;
		}
	}
}

.last-segment {
	padding-top: 80px;
	background-color: #a9a9a9;
}

.active-call-item {
	cursor: pointer;
	transition: background-color 0.3s ease;

	&:hover {
		background-color: rgba(0, 107, 143, 0.1);
	}
}

.ringing-phone {
	animation: ring 1.5s ease-in-out infinite;
	transform-origin: center center;
}

@keyframes ring {
	0% {
		transform: rotate(0deg);
	}
	5% {
		transform: rotate(15deg);
	}
	10% {
		transform: rotate(-13deg);
	}
	15% {
		transform: rotate(14deg);
	}
	20% {
		transform: rotate(-12deg);
	}
	25% {
		transform: rotate(10deg);
	}
	30% {
		transform: rotate(-8deg);
	}
	35% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(0deg);
	}
}

.qr-codes-section {
	margin-top: 10rem;
	text-align: center;
	width: 100%;

	.qr-codes-title {
		color: #006b8f;
		font-size: 1.5rem;
		margin-bottom: 1.5rem;
		font-weight: 600;
	}

	.qr-codes-text {
		color: #555;
		font-size: 1rem;
		margin-bottom: 2.5rem;
	}

	.qr-codes-container {
		display: flex;
		justify-content: center;
		flex-wrap: wrap;
		gap: 2rem;

		@media screen and (max-width: 690px) {
			gap: 1.5rem;
		}
	}

	.qr-code-item {
		margin: 0 4em;
		display: flex;
		flex-direction: column;
		align-items: center;
		background-color: white;
		border-radius: 12px;
		padding: 1rem;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

		.qr-code-image {
			width: 150px;
			height: 150px;
			object-fit: contain;

			@media screen and (max-width: 690px) {
				width: 120px;
				height: 120px;
			}
		}

		.qr-code-label {
			margin-top: 0.75rem;
			font-weight: 600;
			color: #333;
			font-size: 1rem;
		}

		.qr-code-number {
			margin-top: 0.25rem;
			color: #666;
			font-size: 0.875rem;
			font-family: monospace;
		}
	}
}

.tab-title {
	color: #006b8f;
}

.tabs-container {
	background-color: rgb(var(--v-theme-background));
	padding: 0 1rem;
	padding-top: 1.5rem;

	.view-tabs {
		max-width: 800px;
		margin: 20px auto 0 auto;
		border-radius: 12px;
		overflow: hidden;
		background: rgba(0, 107, 143, 0.08);

		.view-tab {
			font-weight: 600;
			letter-spacing: 0.5px;
			text-transform: none;
			font-size: 1rem;
			min-height: 48px;

			@media screen and (max-width: 690px) {
				font-size: 0.875rem;
			}
		}
	}
}
</style>
